import React, { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";
import { PageState } from "@/components/common/PageState";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { hotelKeys } from "@/components/pages/hotels/hooks/useHotels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLayoutRole } from "@/hooks/useLayoutRole";
import { hotelService } from "@/services/hotel-service";
import { ChatSettings } from "@/types/hotel";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { ServiceAutoRepliesSettings } from "@/components/pages/chats/components/ServiceAutoRepliesSettings";

const TEMPERATURE_MIN = 0;
const TEMPERATURE_MAX = 2;
const TEMPERATURE_STEP = 0.1;
const CONFIDENCE_MIN = 0;
const CONFIDENCE_MAX = 1;
const CONFIDENCE_STEP = 0.05;
const RECURSION_LIMIT_MIN = 1;

const DEFAULT_AI_SETTINGS: AiSettingsFormData = {
  textModel: "gpt-4o-mini",
  transcriptionModel: "gpt-4o-mini-transcribe",
  routerTemperature: 0,
  defaultTemperature: 0.7,
  translationTemperature: 0.3,
  recursionLimit: 10,
  confidenceThreshold: 0.7,
};

interface ModelOption {
  value: string;
  label: string;
}

type AiSettingsFormData = {
  textModel: string;
  transcriptionModel: string;
  routerTemperature: number;
  defaultTemperature: number;
  translationTemperature: number;
  recursionLimit: number;
  confidenceThreshold: number;
};

function parseNumericInput(value: unknown): unknown {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.NaN;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim();

    if (!normalizedValue) {
      return undefined;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : Number.NaN;
  }

  return value;
}

function createAiSettingsSchema(t: TFunction): z.ZodType<AiSettingsFormData> {
  const requiredValueMessage = t(
    "hotels.aiSettings.validation.numberRequired",
    {
      defaultValue: "Enter a value.",
    },
  );
  const invalidNumberMessage = t("hotels.aiSettings.validation.numberInvalid", {
    defaultValue: "Enter a valid number.",
  });

  return z.object({
    textModel: z
      .string()
      .trim()
      .min(
        1,
        t("hotels.aiSettings.validation.textModelRequired", {
          defaultValue: "Choose a text model.",
        }),
      ),
    transcriptionModel: z
      .string()
      .trim()
      .min(
        1,
        t("hotels.aiSettings.validation.transcriptionModelRequired", {
          defaultValue: "Choose a transcription model.",
        }),
      ),
    routerTemperature: z.preprocess(
      parseNumericInput,
      z
        .number({
          required_error: requiredValueMessage,
          invalid_type_error: invalidNumberMessage,
        })
        .min(
          TEMPERATURE_MIN,
          t("hotels.aiSettings.validation.routerTemperatureRange", {
            defaultValue: "Router temperature must be between 0 and 2.",
          }),
        )
        .max(
          TEMPERATURE_MAX,
          t("hotels.aiSettings.validation.routerTemperatureRange", {
            defaultValue: "Router temperature must be between 0 and 2.",
          }),
        ),
    ),
    defaultTemperature: z.preprocess(
      parseNumericInput,
      z
        .number({
          required_error: requiredValueMessage,
          invalid_type_error: invalidNumberMessage,
        })
        .min(
          TEMPERATURE_MIN,
          t("hotels.aiSettings.validation.defaultTemperatureRange", {
            defaultValue: "Default temperature must be between 0 and 2.",
          }),
        )
        .max(
          TEMPERATURE_MAX,
          t("hotels.aiSettings.validation.defaultTemperatureRange", {
            defaultValue: "Default temperature must be between 0 and 2.",
          }),
        ),
    ),
    translationTemperature: z.preprocess(
      parseNumericInput,
      z
        .number({
          required_error: requiredValueMessage,
          invalid_type_error: invalidNumberMessage,
        })
        .min(
          TEMPERATURE_MIN,
          t("hotels.aiSettings.validation.translationTemperatureRange", {
            defaultValue: "Translation temperature must be between 0 and 2.",
          }),
        )
        .max(
          TEMPERATURE_MAX,
          t("hotels.aiSettings.validation.translationTemperatureRange", {
            defaultValue: "Translation temperature must be between 0 and 2.",
          }),
        ),
    ),
    recursionLimit: z.preprocess(
      parseNumericInput,
      z
        .number({
          required_error: requiredValueMessage,
          invalid_type_error: invalidNumberMessage,
        })
        .int(
          t("hotels.aiSettings.validation.recursionLimitInteger", {
            defaultValue: "Recursion limit must be a whole number.",
          }),
        )
        .min(
          RECURSION_LIMIT_MIN,
          t("hotels.aiSettings.validation.recursionLimitMin", {
            defaultValue: "Recursion limit must be at least 1.",
          }),
        ),
    ),
    confidenceThreshold: z.preprocess(
      parseNumericInput,
      z
        .number({
          required_error: requiredValueMessage,
          invalid_type_error: invalidNumberMessage,
        })
        .min(
          CONFIDENCE_MIN,
          t("hotels.aiSettings.validation.confidenceThresholdRange", {
            defaultValue: "Confidence threshold must be between 0 and 1.",
          }),
        )
        .max(
          CONFIDENCE_MAX,
          t("hotels.aiSettings.validation.confidenceThresholdRange", {
            defaultValue: "Confidence threshold must be between 0 and 1.",
          }),
        ),
    ),
  });
}

function withCurrentModelOption(
  options: readonly ModelOption[],
  currentValue: string,
  customLabel: string,
): ModelOption[] {
  const normalizedCurrentValue = currentValue.trim();

  if (!normalizedCurrentValue) {
    return [...options];
  }

  if (options.some((option) => option.value === normalizedCurrentValue)) {
    return [...options];
  }

  return [
    {
      value: normalizedCurrentValue,
      label: `${normalizedCurrentValue} (${customLabel})`,
    },
    ...options,
  ];
}

function getAiSettingsDefaults(
  chatSettings?: Partial<ChatSettings> | null,
): AiSettingsFormData {
  return {
    textModel: chatSettings?.textModel || DEFAULT_AI_SETTINGS.textModel,
    transcriptionModel:
      chatSettings?.transcriptionModel ||
      DEFAULT_AI_SETTINGS.transcriptionModel,
    routerTemperature:
      chatSettings?.routerTemperature ?? DEFAULT_AI_SETTINGS.routerTemperature,
    defaultTemperature:
      chatSettings?.defaultTemperature ??
      DEFAULT_AI_SETTINGS.defaultTemperature,
    translationTemperature:
      chatSettings?.translationTemperature ??
      DEFAULT_AI_SETTINGS.translationTemperature,
    recursionLimit:
      chatSettings?.recursionLimit ?? DEFAULT_AI_SETTINGS.recursionLimit,
    confidenceThreshold:
      chatSettings?.confidenceThreshold ??
      DEFAULT_AI_SETTINGS.confidenceThreshold,
  };
}

export const AiSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const aiSettingsSchema = useMemo(() => createAiSettingsSchema(t), [t]);
  const queryClient = useQueryClient();
  const selectedHotelId = useSelectedHotelId();
  const layoutRole = useLayoutRole();
  const isHotelScopedRole = layoutRole === "HOTEL_SCOPED";
  usePageTitle();

  const userHotelQuery = useQuery({
    queryKey: ["userHotel", selectedHotelId],
    queryFn: () => hotelService.getUserHotel(),
    enabled: isHotelScopedRole && !selectedHotelId,
  });

  const hotelId = selectedHotelId ?? userHotelQuery.data?.id ?? null;
  const hotelQuery = useQuery({
    queryKey: hotelKeys.detail(hotelId ?? ""),
    queryFn: () => hotelService.getHotelById(hotelId!),
    enabled: !!hotelId,
  });

  const hotel = hotelQuery.data ?? userHotelQuery.data ?? null;
  const isLoading = userHotelQuery.isLoading || hotelQuery.isLoading;

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<AiSettingsFormData>({
    resolver: zodResolver(aiSettingsSchema),
    defaultValues: DEFAULT_AI_SETTINGS,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const textModel = watch("textModel");
  const transcriptionModel = watch("transcriptionModel");
  const baseTextModelOptions = useMemo<ModelOption[]>(
    () => [
      {
        value: "gpt-4o-mini",
        label: t("hotels.aiSettings.modelOptions.text.gpt4oMini", {
          defaultValue:
            "GPT-4o mini - balanced and cost-efficient (recommended)",
        }),
      },
      {
        value: "gpt-4.1-mini",
        label: t("hotels.aiSettings.modelOptions.text.gpt41Mini", {
          defaultValue: "GPT-4.1 mini - stronger reasoning for harder cases",
        }),
      },
    ],
    [t],
  );
  const baseTranscriptionModelOptions = useMemo<ModelOption[]>(
    () => [
      {
        value: "gpt-4o-mini-transcribe",
        label: t(
          "hotels.aiSettings.modelOptions.transcription.gpt4oMiniTranscribe",
          {
            defaultValue:
              "GPT-4o mini transcribe - faster and cost-efficient (recommended)",
          },
        ),
      },
      {
        value: "gpt-4o-transcribe",
        label: t(
          "hotels.aiSettings.modelOptions.transcription.gpt4oTranscribe",
          {
            defaultValue: "GPT-4o transcribe - higher transcription accuracy",
          },
        ),
      },
      {
        value: "whisper-1",
        label: t("hotels.aiSettings.modelOptions.transcription.whisper1", {
          defaultValue: "Whisper-1 - legacy compatibility option",
        }),
      },
    ],
    [t],
  );
  const textModelOptions = useMemo(
    () =>
      withCurrentModelOption(
        baseTextModelOptions,
        textModel,
        t("hotels.aiSettings.currentSavedValue", {
          defaultValue: "saved custom value",
        }),
      ),
    [baseTextModelOptions, textModel, t],
  );
  const transcriptionModelOptions = useMemo(
    () =>
      withCurrentModelOption(
        baseTranscriptionModelOptions,
        transcriptionModel,
        t("hotels.aiSettings.currentSavedValue", {
          defaultValue: "saved custom value",
        }),
      ),
    [baseTranscriptionModelOptions, transcriptionModel, t],
  );

  useEffect(() => {
    if (hotel) {
      reset(getAiSettingsDefaults(hotel.chatSettings));
      void trigger();
    }
  }, [hotel, reset, trigger]);

  const updateAiSettings = useMutation({
    mutationFn: (data: AiSettingsFormData) =>
      hotelService.updateHotel(hotelId!, {
        chatSettings: data,
      }),
    onSuccess: (updatedHotel) => {
      queryClient.setQueryData(hotelKeys.detail(updatedHotel.id), updatedHotel);
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
      reset(getAiSettingsDefaults(updatedHotel.chatSettings));
      void trigger();
      toast.success(
        t("hotels.aiSettings.saveSuccess", {
          defaultValue: "AI settings saved",
        }),
      );
    },
    onError: () => {
      toast.error(
        t("hotels.aiSettings.saveError", {
          defaultValue: "Could not save AI settings",
        }),
      );
    },
  });

  if (!hotelId && !isLoading) {
    return (
      <PageState
        text={t("hotels.aiSettings.hotelNotSelected", {
          defaultValue: "Choose a hotel to edit AI settings.",
        })}
      />
    );
  }

  if (isLoading) {
    return (
      <PageState
        text={t("hotels.aiSettings.loading", {
          defaultValue: "Loading AI settings...",
        })}
      />
    );
  }

  if (!hotel) {
    return (
      <PageState
        tone="error"
        text={t("hotels.aiSettings.loadingError", {
          defaultValue: "Could not load AI settings.",
        })}
      />
    );
  }

  const isSaving = updateAiSettings.isPending;

  return (
    <div className="p-12">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-semibold">
            {t("hotels.aiSettings.title", { defaultValue: "AI Settings" })}
          </h1>
          <p className="text-muted-foreground text-sm">{hotel.name}</p>
          <p className="text-muted-foreground text-sm">
            {t("hotels.aiSettings.description", {
              defaultValue:
                "These settings control how AI replies, transcribes voice messages, and translates guest conversations. If you are unsure, keep the recommended values.",
            })}
          </p>
        </div>

        <form
          className="grid grid-cols-1 gap-5 md:grid-cols-2"
          onSubmit={handleSubmit((data) => updateAiSettings.mutate(data))}
        >
          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold">
              {t("hotels.aiSettings.sections.models", {
                defaultValue: "Models",
              })}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.sections.modelsDescription", {
                defaultValue:
                  "Choose which AI models handle text replies and voice transcription.",
              })}
            </p>
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="textModel">
              {t("hotels.aiSettings.textModel", {
                defaultValue: "AI Reply Model",
              })}
            </Label>
            <Controller
              name="textModel"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="textModel"
                    className="h-10 w-full justify-between"
                  >
                    <SelectValue
                      placeholder={t("hotels.aiSettings.textModel", {
                        defaultValue: "Text Model",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {textModelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.textModelHint", {
                defaultValue:
                  "Used for AI answers, chat suggestions, and deciding how the system should respond.",
              })}
            </p>
            {errors.textModel?.message ? (
              <p className="text-sm text-red-500">{errors.textModel.message}</p>
            ) : null}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="transcriptionModel">
              {t("hotels.aiSettings.transcriptionModel", {
                defaultValue: "Voice Transcription Model",
              })}
            </Label>
            <Controller
              name="transcriptionModel"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="transcriptionModel"
                    className="h-10 w-full justify-between"
                  >
                    <SelectValue
                      placeholder={t("hotels.aiSettings.transcriptionModel", {
                        defaultValue: "Transcription Model",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {transcriptionModelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.transcriptionModelHint", {
                defaultValue:
                  "Used to turn guest voice messages into text before staff or AI reads them.",
              })}
            </p>
            {errors.transcriptionModel?.message ? (
              <p className="text-sm text-red-500">
                {errors.transcriptionModel.message}
              </p>
            ) : null}
          </div>

          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold">
              {t("hotels.aiSettings.sections.behavior", {
                defaultValue: "Behavior",
              })}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.sections.behaviorDescription", {
                defaultValue:
                  "Fine-tune how stable, creative, or literal AI responses should be.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="routerTemperature">
              {t("hotels.aiSettings.routerTemperature", {
                defaultValue: "Decision Consistency",
              })}
            </Label>
            <Input
              id="routerTemperature"
              type="number"
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              {...register("routerTemperature")}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.routerTemperatureHint", {
                defaultValue:
                  "Controls how consistently AI chooses the same response path. Lower values are more stable. Range: 0.0-2.0. Recommended: 0.0.",
              })}
            </p>
            {errors.routerTemperature?.message ? (
              <p className="text-sm text-red-500">
                {errors.routerTemperature.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="defaultTemperature">
              {t("hotels.aiSettings.defaultTemperature", {
                defaultValue: "Reply Creativity",
              })}
            </Label>
            <Input
              id="defaultTemperature"
              type="number"
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              {...register("defaultTemperature")}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.defaultTemperatureHint", {
                defaultValue:
                  "Controls how creative or varied AI replies can be. Lower values are more predictable. Range: 0.0-2.0. Recommended: 0.7.",
              })}
            </p>
            {errors.defaultTemperature?.message ? (
              <p className="text-sm text-red-500">
                {errors.defaultTemperature.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="translationTemperature">
              {t("hotels.aiSettings.translationTemperature", {
                defaultValue: "Translation Flexibility",
              })}
            </Label>
            <Input
              id="translationTemperature"
              type="number"
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              {...register("translationTemperature")}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.translationTemperatureHint", {
                defaultValue:
                  "Controls how literal or natural translations should feel. Lower values stay closer to the original wording. Range: 0.0-2.0. Recommended: 0.3.",
              })}
            </p>
            {errors.translationTemperature?.message ? (
              <p className="text-sm text-red-500">
                {errors.translationTemperature.message}
              </p>
            ) : null}
          </div>

          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold">
              {t("hotels.aiSettings.sections.safety", {
                defaultValue: "Safety and Limits",
              })}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.sections.safetyDescription", {
                defaultValue:
                  "Control how cautious AI should be and how far it may go while building an answer.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recursionLimit">
              {t("hotels.aiSettings.recursionLimit", {
                defaultValue: "Maximum AI Decision Steps",
              })}
            </Label>
            <Input
              id="recursionLimit"
              type="number"
              min={RECURSION_LIMIT_MIN}
              step={1}
              {...register("recursionLimit")}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.recursionLimitHint", {
                defaultValue:
                  "Limits how many internal steps AI may use before it stops. Higher values may help in harder cases but can be slower. Whole numbers only. Minimum: 1. Recommended: 10.",
              })}
            </p>
            {errors.recursionLimit?.message ? (
              <p className="text-sm text-red-500">
                {errors.recursionLimit.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confidenceThreshold">
              {t("hotels.aiSettings.confidenceThreshold", {
                defaultValue: "Minimum Confidence for Auto-Reply",
              })}
            </Label>
            <Input
              id="confidenceThreshold"
              type="number"
              min={CONFIDENCE_MIN}
              max={CONFIDENCE_MAX}
              step={CONFIDENCE_STEP}
              {...register("confidenceThreshold")}
            />
            <p className="text-muted-foreground text-sm">
              {t("hotels.aiSettings.confidenceThresholdHint", {
                defaultValue:
                  "AI should answer automatically only when it is at least this confident. Higher values are safer, lower values allow more automatic replies. Range: 0.0-1.0. Recommended: 0.7.",
              })}
            </p>
            {errors.confidenceThreshold?.message ? (
              <p className="text-sm text-red-500">
                {errors.confidenceThreshold.message}
              </p>
            ) : null}
          </div>

          <div className="flex justify-end md:col-span-2">
            <Button type="submit" disabled={isSaving || !hotelId || !isValid}>
              <Save className="size-4" />
              {isSaving
                ? t("hotels.aiSettings.saving", { defaultValue: "Saving..." })
                : t("hotels.aiSettings.save", {
                    defaultValue: "Save settings",
                  })}
            </Button>
          </div>
        </form>

        <ServiceAutoRepliesSettings hotelId={hotel.id} />
      </div>
    </div>
  );
};
